import { IChatRecord } from "components/UI/Chat/AcxChatViewer";
import BaseEntity from "./BaseEntity";
import type {
    ConversationClassifier,
    FocusedConversationInsightSource,
    SingleConversationData,
} from "services/ConversationService";
import { InteractionType } from "./InteractionType";
import { IMultiLanguageChatTranscriptionPayload } from "services/ChatMediaService";
import {
    ENGLISH_LANGUAGE_CODE,
    IMultiLanguageTranscriptionPayload,
    ITranscriptionPayload,
    ITranscriptionSegment,
} from "services/TranscriptionService";
import { computed, makeObservable, observable } from "mobx";
import { AdverseEventP2Results } from "./AdverseEventP2Results";
import { EddyEffectP2Results } from "./EddyEffectP2Results";
import { emptyGUID } from "components/Admin/Organizations/OrganizationDetail/OrganizationModules/OrganizationModuleDetails/OrganizationModuleQuestionDependencies";
import { getTranscriptByLangCode } from "utils/Utils";
import { ML_AdverseEvents_P2 } from "components/Conversations/Views/Drawer/components/ConversationClassifierChipList/Helpers";
import { EvalType } from "components/Conversations/Stores/ConversationsStore";

export default class Conversation extends BaseEntity {
    callDurationMillis: number;
    conversationClassifiers: ConversationClassifier[];
    conversationId: string;
    evalType: EvalType | null;
    mediaType: InteractionType;
    summary: string;
    timestamp: string;
    transcriptions: ITranscriptionPayload[];
    agentFirstName: string;
    agentLastName: string;
    evaluationId?: string;
    isRedacted: boolean;
    evaluationNumber: number;
    evaluationAgentCoachingId?: string;
    agentId: string;
    hierarchyId: string;
    callDirection: string;
    workflowInstanceIds?: string[];
    focusedConversationInsightSource: FocusedConversationInsightSource;

    @observable multiLangTranscriptions?: IMultiLanguageTranscriptionPayload[];
    @observable
    multiLanguageChatTranscription?: IMultiLanguageChatTranscriptionPayload[];
    @observable
    languageCode: IMultiLanguageTranscriptionPayload["languageCode"] = "en";

    constructor(conversation: SingleConversationData) {
        super();
        makeObservable(this);

        this.agentFirstName = conversation.agentFirstName;
        this.agentLastName = conversation.agentLastName;
        this.callDurationMillis = conversation.callDurationMillis;
        this.multiLanguageChatTranscription =
            conversation.multiLanguageChatTranscription;
        this.conversationClassifiers = conversation.conversationClassifiers;
        this.conversationId = conversation.conversationId;
        this.evalType = conversation.evalType;
        this.evaluationId = conversation.evaluationId;
        this.evaluationNumber = conversation.evaluationNumber;
        this.evaluationAgentCoachingId = conversation.evaluationAgentCoachingId;
        this.agentId = conversation.agentId;
        this.hierarchyId = conversation.hierarchyId;
        this.callDirection = conversation.callDirection;
        this.isRedacted = conversation.isRedacted;
        this.mediaType = conversation.mediaType;
        this.summary = conversation.summary;
        this.timestamp = conversation.timestamp;
        this.transcriptions = conversation.transcriptions;
        this.focusedConversationInsightSource =
            conversation.focusedConversationInsightSource ?? null;
    }

    @computed
    get hasEval() {
        return this?.evaluationId !== emptyGUID || this.evalType !== null;
    }

    @computed
    get isChat(): boolean {
        return (
            this.mediaType === InteractionType.Chat ||
            this.mediaType === InteractionType.ProcessedChat
        );
    }

    @computed
    get chatTranscription(): IChatRecord[] {
        if (this.multiLanguageChatTranscription?.length) {
            const chatForLanguage = this.multiLanguageChatTranscription.find(
                (item) => item.languageCode === this.languageCode,
            )?.chatTranscription;

            if (chatForLanguage) {
                return chatForLanguage;
            }

            const englishChat = this.multiLanguageChatTranscription.find(
                (item) => item.languageCode === ENGLISH_LANGUAGE_CODE,
            )?.chatTranscription;

            return (
                englishChat ??
                this.multiLanguageChatTranscription[0]?.chatTranscription ??
                []
            );
        }

        return [];
    }

    @computed
    get eddyData(): EddyEffectP2Results[] {
        const eddyData = this.conversationClassifiers.find(
            (item) => item.name === "ML-EddyEffect-P2",
        );

        if (eddyData) {
            const parsedResult: number[][] | null = JSON.parse(eddyData.result);
            if (!parsedResult) {
                return [];
            } else {
                // translate to structure of other endpoint getEddyEffectP2Results
                return parsedResult.map((item) => ({
                    startWordIndex: item[0],
                    endWordIndex: item[1],
                }));
            }
        }
        return [];
    }

    @computed
    get adverseEventData(): AdverseEventP2Results[] {
        const adverseEventData = this.conversationClassifiers.find(
            (item) => item.name === ML_AdverseEvents_P2,
        );
        if (adverseEventData) {
            const parsedResult: number[][] | null = JSON.parse(
                adverseEventData.result,
            );

            if (!parsedResult) {
                return [];
            } else {
                // translate to structure of other endpoint getEddyEffectP2Results
                return parsedResult.map((item) => ({
                    startWordIndex: item[0],
                    endWordIndex: item[1],
                }));
            }
        }
        return [];
    }

    @computed
    get hasSafetyEvent(): { result: boolean; containsExamples: boolean } {
        const safetyEventData = this.conversationClassifiers.find(
            (item) => item.name === "ML-SE-Identification",
        );
        return {
            result: safetyEventData?.result === "true",
            containsExamples: this.adverseEventData.length > 0,
        };
    }

    @computed
    get hasEddyEffect(): { result: boolean; containsExamples: boolean } {
        const eddyData = this.conversationClassifiers.find(
            (item) => item.name === "ML-EddyEffect",
        );
        return {
            result: eddyData?.result === "true",
            containsExamples: this.eddyData.length > 0,
        };
    }

    @computed
    get audioTranscription():
        | {
              phrases?: ITranscriptionSegment[];
              words?: ITranscriptionSegment[];
          }
        | undefined {
        return getTranscriptByLangCode(
            this.languageCode,
            this.multiLangTranscriptions,
        );
    }

    @computed get hasLLMClassifierResults() {
        return this.conversationClassifiers.some(
            (c) => c.classifierType === "LLM",
        );
    }
}
